import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Card, 
  CardActions, 
  CardContent, 
  CardMedia, 
  Container, 
  Grid, 
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
    '& .MuiCardMedia-root': {
      transform: 'scale(1.05)',
    },
  },
  position: 'relative',
  overflow: 'hidden',
}));

const CardIconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: '2rem',
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  paddingTop: '56.25%', // 16:9 aspect ratio
  position: 'relative',
  transition: 'transform 0.5s ease',
}));

const services = [
  {
    id: 1,
    title: 'Delivery Services',
    description: 'Professional delivery of furniture, appliances, and more throughout Northeast Wisconsin. Fast, reliable, and careful handling guaranteed.',
    icon: <LocalShippingIcon fontSize="large" />,
    image: '/assets/images/delivery-service.jpg',
    link: '/services/delivery',
    features: ['Appliance Delivery', 'Furniture Delivery', 'Haul Aways'],
  },
  {
    id: 2,
    title: 'Junk Removal',
    description: 'Comprehensive junk removal services for homes and businesses. We handle all types of waste and ensure eco-friendly disposal.',
    icon: <DeleteIcon fontSize="large" />,
    image: '/assets/images/junk-removal.jpg',
    link: '/services/junk-removal',
    features: ['Decluttering', 'Property Cleanouts', 'Responsible Disposal'],
  },
  {
    id: 3,
    title: 'Moving Services',
    description: 'Full-service moving solutions for residential and commercial relocations. Let us handle the heavy lifting for a stress-free move.',
    icon: <HomeIcon fontSize="large" />,
    image: '/assets/images/moving-service.jpg',
    link: '/services/moving',
    features: ['Residential Moving', 'Commercial Moving', 'Packing Services'],
  },
];

interface ServicesOverviewProps {
  showHeading?: boolean;
  limitServices?: number;
  hideFeatures?: boolean;
}

const ServicesOverview: React.FC<ServicesOverviewProps> = ({ 
  showHeading = true,
  limitServices,
  hideFeatures = false
}) => {
  const theme = useTheme();
  const displayedServices = limitServices ? services.slice(0, limitServices) : services;

  return (
    <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container>
        {showHeading && (
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h2"
              component="h2"
              fontWeight={700}
              gutterBottom
            >
              Our Services
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: '800px', mx: 'auto' }}
            >
              JBR Delivery LLC offers a comprehensive range of delivery, junk removal, and moving services throughout Northeast Wisconsin.
            </Typography>
          </Box>
        )}

        <Grid container spacing={4}>
          {displayedServices.map((service) => (
            <Grid item xs={12} md={4} key={service.id}>
              <ServiceCard>
                <StyledCardMedia
                  image={service.image}
                  title={service.title}
                />
                <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                  <CardIconWrapper>
                    {service.icon}
                  </CardIconWrapper>
                  <Typography 
                    gutterBottom 
                    variant="h5" 
                    component="h3"
                    fontWeight={600}
                  >
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {service.description}
                  </Typography>
                  
                  {!hideFeatures && (
                    <Box mt={2}>
                      {service.features.map((feature, index) => (
                        <Typography 
                          key={index} 
                          variant="body2" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            mb: 0.5,
                            '&::before': {
                              content: '""',
                              display: 'inline-block',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: theme.palette.primary.main,
                              marginRight: '8px',
                            }
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    component={RouterLink}
                    to={service.link}
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ fontWeight: 600 }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
        
        {limitServices && limitServices < services.length && (
          <Box textAlign="center" mt={6}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              component={RouterLink}
              to="/services"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.2, fontWeight: 600 }}
            >
              View All Services
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ServicesOverview;
